import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Social Login Button component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 234:3200):
 * - Sizes: sm (32px height), md (36px height)
 * - States: Normal, Hover, Pressed, Focus, Disabled
 * - Full width (320px default)
 * - Secondary button styling with brand icon
 */
const socialLoginButtonVariants = cva(
  'inline-flex w-full items-center justify-center gap-2 rounded-lg text-sm font-medium transition-all duration-100 focus:outline-none disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        sm: 'h-8 px-3 py-1.5',
        md: 'h-9 px-3.5 py-2',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

// Brand icons
const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.68 8.18c0-.57-.05-1.11-.15-1.64H8v3.1h4.31c-.19 1-.75 1.85-1.59 2.42v2h2.58c1.51-1.39 2.38-3.44 2.38-5.88z" fill="#4285F4"/>
    <path d="M8 16c2.16 0 3.97-.72 5.29-1.94l-2.58-2c-.72.48-1.63.76-2.71.76-2.08 0-3.85-1.41-4.48-3.3H.85v2.07A7.998 7.998 0 0 0 8 16z" fill="#34A853"/>
    <path d="M3.52 9.52a4.8 4.8 0 0 1 0-3.04V4.41H.85a7.998 7.998 0 0 0 0 7.18l2.67-2.07z" fill="#FBBC05"/>
    <path d="M8 3.18c1.17 0 2.23.41 3.06 1.2l2.29-2.29A7.96 7.96 0 0 0 8 0C4.89 0 2.17 1.79.85 4.41l2.67 2.07C4.15 4.59 5.92 3.18 8 3.18z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.67 8.43c-.02-1.85 1.51-2.74 1.58-2.79-.86-1.26-2.2-1.43-2.67-1.45-1.14-.12-2.22.67-2.8.67-.58 0-1.47-.65-2.42-.63-1.24.02-2.39.72-3.03 1.84-1.29 2.24-.33 5.56.93 7.38.62.89 1.35 1.89 2.32 1.86.93-.04 1.28-.6 2.4-.6 1.12 0 1.44.6 2.42.58.99-.02 1.63-.91 2.24-1.81.71-1.04.99-2.04 1.01-2.09-.02-.01-1.95-.75-1.98-2.96zM10.82 2.72c.51-.62.86-1.48.76-2.34-.74.03-1.63.49-2.16 1.11-.47.55-.89 1.43-.78 2.27.82.07 1.66-.42 2.18-1.04z" fill="currentColor"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 7.5H0V0h7.5v7.5z" fill="#F25022"/>
    <path d="M16 7.5H8.5V0H16v7.5z" fill="#7FBA00"/>
    <path d="M7.5 16H0V8.5h7.5V16z" fill="#00A4EF"/>
    <path d="M16 16H8.5V8.5H16V16z" fill="#FFB900"/>
  </svg>
);

const brandIcons = {
  google: GoogleIcon,
  apple: AppleIcon,
  microsoft: MicrosoftIcon,
};

const brandLabels = {
  google: 'Continue with Google',
  apple: 'Continue with Apple',
  microsoft: 'Continue with Microsoft',
};

const SocialLoginButton = React.forwardRef(
  ({ className, size, brand = 'google', label, disabled, onClick, ...props }, ref) => {
    const BrandIcon = brandIcons[brand] || brandIcons.google;
    const buttonLabel = label || brandLabels[brand] || `Continue with ${brand}`;

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={cn(
          socialLoginButtonVariants({ size }),
          'bg-white shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)]',
          'hover:bg-grey-50 hover:shadow-[0px_1px_1px_0px_rgba(18,18,18,0.2),0px_0px_0px_1px_rgba(18,18,18,0.1),0px_1px_3px_0px_rgba(18,18,18,0.1)]',
          'active:bg-grey-100',
          'focus:shadow-[0px_0px_0px_4px_rgba(18,18,18,0.1),0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)]',
          'disabled:bg-white disabled:text-black/40 disabled:shadow-[0px_1px_1px_0px_rgba(18,18,18,0.05),0px_0px_0px_1px_rgba(18,18,18,0.04),0px_1px_3px_0px_rgba(18,18,18,0.05)]',
          'dark:bg-grey-900 dark:shadow-[0px_1px_1px_0px_rgba(255,255,255,0.05),0px_0px_0px_1px_rgba(255,255,255,0.1)]',
          className
        )}
        {...props}
      >
        <BrandIcon />
        <span className={cn(
          'text-grey-950 dark:text-grey-50',
          disabled && 'text-black/40 dark:text-white/40'
        )}>
          {buttonLabel}
        </span>
      </button>
    );
  }
);
SocialLoginButton.displayName = 'SocialLoginButton';

export { SocialLoginButton, socialLoginButtonVariants, GoogleIcon, AppleIcon, MicrosoftIcon };
